import React, { useEffect, useState } from "react";
import { Form, Upload, Divider, Select, Table, Tag, Alert, message } from "antd";
import * as XLSX from "xlsx";
import {
  NextButton, PreviousButton, ButtonComponent, DownloadButton, TableComponent, SaveButton,
} from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import { getEmployeesReq, importEmployeeDataReq, onDownloadSampleEmployeesReq } from "../requests";
import ReadinessSummary from "./ReadinessSummary";
import AddEmployee from "@mods/employeeManagement/companyEmployees/components/AddEmployee";

const formName = "onbEmployees";
const PREVIEW_ROWS = 50;
const employeeFilters = "employee_status_|active";

// server-side import error table (row/column/value/message)
const errorColumns = [
  { key: "row", title: "Row", dataIndex: "row" },
  { key: "column", title: "Column", dataIndex: "column" },
  { key: "value", title: "Value", dataIndex: "value" },
  {
    key: "message",
    title: "Message",
    render: (record) => {
      const m = record.message || "";
      if (m.includes("out of bounds")) {
        const matches = [...m.matchAll(/\[(.*?)\]/g)];
        if (matches.length) return "Sheet not found. Create/rename sheet to " + matches.map((x) => x[1]).join(", ");
      }
      return m;
    },
  },
];

// Step "Import Employees" — download template, upload a sheet, preview the
// parsed rows in-browser (SheetJS, no backend dry-run needed), then commit to
// the real employee import. Import is required to continue.
const Step3Employees = (props) => {
  const { wizard, patch, onNext, onBack, readiness, readinessLoading, refreshReadiness, statuses } = props;
  const [fileList, setFileList] = useState([]);
  const [sheets, setSheets] = useState([]);        // [{name, headers, rows}]
  const [activeSheet, setActiveSheet] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [errorNode, setErrorNode] = useState(null);
  const [imported, setImported] = useState(!!wizard.employees?.imported || readiness.employees.count > 0);
  const [childComponent, setChildComponent] = useState(null);
  const [employeeRows, setEmployeeRows] = useState(readiness.employees.rows || []);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeePagination, setEmployeePagination] = useState({
    current: readiness.employees.pagination?.current_page || 1,
    pageSize: readiness.employees.pagination?.per_page || 10,
    total: readiness.employees.count || 0,
  });
  const existingCount = readiness.employees.count || 0;
  const canContinue = imported || existingCount > 0;

  const normalizeEmployeeResponse = (data) => {
    const resource = data?.employee_employments || {};
    const pagination = resource?.meta?.pagination || resource?.pagination || {};
    return {
      rows: Array.isArray(resource.data) ? resource.data : [],
      pagination: {
        current: pagination.current_page || 1,
        pageSize: pagination.per_page || 10,
        total: pagination.total || (Array.isArray(resource.data) ? resource.data.length : 0),
      },
    };
  };

  const loadEmployees = (page = 1) => {
    setEmployeeLoading(true);
    makeRequestStateless(
      getEmployeesReq,
      { page, filters: employeeFilters, showActive: true },
      (data) => {
        const next = normalizeEmployeeResponse(data);
        setEmployeeRows(next.rows);
        setEmployeePagination(next.pagination);
        setEmployeeLoading(false);
      },
      () => {
        setEmployeeRows([]);
        setEmployeeLoading(false);
      }
    );
  };

  useEffect(() => {
    setEmployeeRows(readiness.employees.rows || []);
    setEmployeePagination({
      current: readiness.employees.pagination?.current_page || 1,
      pageSize: readiness.employees.pagination?.per_page || 10,
      total: readiness.employees.count || 0,
    });
  }, [readiness.employees.rows, readiness.employees.pagination, readiness.employees.count]);

  useEffect(() => {
    if (existingCount > 0) loadEmployees(1);
    // eslint-disable-next-line
  }, [existingCount]);

  const downloadTemplate = () => {
    onDownloadSampleEmployeesReq()
      .then((blob) => {
        if (!blob) return;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement("a");
        a.href = url;
        a.download = "employees-template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Could not download template"));
  };

  // ---- client-side parse for preview ------------------------------------
  const parseWorkbook = async (file) => {
    setParsing(true);
    setErrorNode(null);
    setImported(false);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const parsed = wb.SheetNames.map((name) => {
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false, defval: "" });
        const rawHeaders = aoa[0] || [];
        // A worksheet's used range is often wider than the actual data, which
        // makes sheet_to_json pad rows with empty trailing cells. Keep only the
        // columns up to the last named header so the preview doesn't show
        // phantom "Column N" columns that don't exist in the sheet.
        let lastCol = -1;
        rawHeaders.forEach((h, i) => { if (String(h).trim() !== "") lastCol = i; });
        const headers = rawHeaders.slice(0, lastCol + 1).map((h) => String(h));
        const rows = aoa.slice(1).map((row) => row.slice(0, lastCol + 1));
        return { name, headers, rows };
      }).filter((s) => s.headers.length);
      setSheets(parsed);
      const primary = parsed.find((s) => /employee|company/i.test(s.name)) || parsed[0];
      setActiveSheet(primary ? primary.name : null);
    } catch (e) {
      message.error("Could not read that file — is it a valid .xlsx?");
      setSheets([]);
      setActiveSheet(null);
    }
    setParsing(false);
  };

  const onChange = ({ fileList: list }) => {
    const trimmed = list.slice(-1); // single file
    setFileList(trimmed);
    const f = trimmed[0]?.originFileObj;
    if (f) parseWorkbook(f);
    else { setSheets([]); setActiveSheet(null); }
  };

  const onRemove = () => { setFileList([]); setSheets([]); setActiveSheet(null); setImported(false); setErrorNode(null); };

  // ---- commit to the real import ----------------------------------------
  const onImport = () => {
    const file = fileList[0]?.originFileObj;
    if (!file) { message.info("Please select a file to import"); return; }
    const payload = {
      file_attachment: file,
      employees_import: { attachment: { removed: false, org_name: file.name, key: "attachment" } },
    };
    setImporting(true);
    makeRequestStateless(importEmployeeDataReq, payload, onSuccess, onError);
  };

  const onSuccess = (data, res) => {
    setImporting(false);
    const rows = Array.isArray(data) ? data : data?.errors;
    if (rows && rows.length > 0) {
      setImported(false);
      setErrorNode(
        <>
          <Divider className="text-danger">Import errors — fix and re-upload</Divider>
          <TableComponent scroll={{ x: "max-content" }} columns={errorColumns} dataSource={rows} pagination={false} />
        </>
      );
      return;
    }
    // No errors — but confirm records were actually created before claiming
    // success. `imported` is the count the backend committed; 0 means the file
    // parsed but nothing was created (e.g. blank rows, or sheet/column names
    // that don't match the template).
    const imported = data?.imported;
    if (imported === 0) {
      setImported(false);
      setErrorNode(
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="No employees were imported"
          description="The file was read but no employee rows were created. Check that the sheet is named 'Employees' and that the required columns (Salutation, First Name, Gender, Mobile No, Nationality, plus employment details like Department, Designation, Grade, Joining Date, Employee Type, Contract Type and Prev Code) are filled in."
        />
      );
      return;
    }
    setErrorNode(null);
    setImported(true);
    message.success(imported ? `${imported} employee${imported === 1 ? "" : "s"} imported` : (res?.message || "Employees imported"));
    patch({ employees: { imported: true, count: imported } });
    refreshReadiness?.();
    loadEmployees(1);
  };

  const onError = (err, res) => {
    setImporting(false);
    message.error(res?.response?.data?.message || "Server error during import");
  };

  const proceed = () => {
    if (!canContinue) { message.info("Add or import employees to continue."); return; }
    if (existingCount > 0 && !wizard.employees?.imported) {
      patch({ employees: { existing: true, count: existingCount } });
    }
    onNext();
  };

  const useExisting = () => {
    if (!existingCount) return;
    setImported(true);
    patch({ employees: { existing: true, count: existingCount } });
    message.success(`${existingCount} existing employee${existingCount === 1 ? "" : "s"} selected for setup`);
  };

  const onManualEmployeeDone = (data) => {
    setChildComponent(null);
    if (data !== false) {
      setImported(true);
      patch({ employees: { existing: true, count: Math.max(existingCount, 1) } });
      refreshReadiness?.();
      loadEmployees(1);
      message.success("Employee added. Setup data is refreshing.");
    }
  };

  const openManualEmployee = () => {
    setChildComponent(<AddEmployee onCompleted={onManualEmployeeDone} />);
  };

  // ---- preview table for the active sheet -------------------------------
  const active = sheets.find((s) => s.name === activeSheet);
  const previewColumns = active
    ? active.headers.map((h, i) => ({ title: h, dataIndex: `c${i}`, key: `c${i}`, ellipsis: true }))
    : [];
  const previewData = active
    ? active.rows.slice(0, PREVIEW_ROWS).map((row, ri) => {
        const obj = { key: ri };
        active.headers.forEach((_, ci) => { obj[`c${ci}`] = row[ci] ?? ""; });
        return obj;
      })
    : [];

  const openEmployeeProfile = (record) => {
    const id = record.id || record.employment_id;
    if (!id) return;
    window.open(`/employees/profile/${id}`, "_blank", "noopener,noreferrer");
  };

  const employeeColumns = [
    { title: "Employee", render: (record) => record.full_name || record.employee_name || record.name || record.emp_name || "Employee" },
    { title: "Code", render: (record) => record.emp_code || record.code || record.prev_code || "-" },
    { title: "Department", render: (record) => record.department_name || record.department || "-" },
    { title: "Designation", render: (record) => record.designation_name || record.designation || "-" },
  ];

  return (
    <>
      <div className="onb-card-title">Import your employees</div>
      <div className="onb-card-sub">
        Review existing employees first. If no employees are found, import them using the template or add them manually from People.
      </div>

      <ReadinessSummary
        status={statuses.employees}
        loading={readinessLoading}
        onRefresh={refreshReadiness}
        items={[
          { label: "Active employees", value: existingCount },
          { label: "Employee setup", value: canContinue ? "Ready" : "Pending" },
        ]}
      />

      {existingCount > 0 ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="success"
          showIcon
          message={`${existingCount} active employee${existingCount === 1 ? "" : "s"} found`}
          description="You can continue with the employees already in the system, or upload another file if you need to add more."
          action={<ButtonComponent type="primary" text="Use existing employees" onClick={useExisting} />}
        />
      ) : (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="No employees found"
          description="Import employees to continue setup, or add employees manually from the People module."
          action={<ButtonComponent text="Add manually" onClick={openManualEmployee} />}
        />
      )}

      <div className="onb-note">
        The template's sheet and column names are what KLOK maps on import. Preview below is read in your browser; nothing is sent until you click <b>Import employees</b>.
      </div>

      <Form name={formName}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <DownloadButton onClick={downloadTemplate} text="Download Template" />
        </div>
        <Form.Item>
          <Upload
            accept=".xlsx"
            listType="picture"
            fileList={fileList}
            onChange={onChange}
            onRemove={onRemove}
            multiple={false}
            beforeUpload={() => false}
          >
            {fileList.length === 1 ? null : <ButtonComponent text="Upload File" />}
          </Upload>
        </Form.Item>
      </Form>

      {/* Preview */}
      {sheets.length > 0 && active && (
        <div style={{ marginTop: 6 }}>
          <div className="onb-section-h" style={{ justifyContent: "space-between", display: "flex" }}>
            <span>Preview — {active.name}</span>
            <span style={{ fontWeight: 500, fontSize: 12, color: "#756e62" }}>
              {active.rows.length} row{active.rows.length === 1 ? "" : "s"}
              {active.rows.length > PREVIEW_ROWS ? ` (showing first ${PREVIEW_ROWS})` : ""}
            </span>
          </div>
          <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "#756e62" }}>Sheet:</span>
            <Select
              size="small"
              value={activeSheet}
              style={{ minWidth: 200 }}
              onChange={setActiveSheet}
              options={sheets.map((s) => ({ value: s.name, label: `${s.name} (${s.rows.length})` }))}
            />
            {sheets.map((s) => (
              <Tag key={s.name} color={s.name === activeSheet ? "orange" : "default"}>{s.name}: {s.rows.length}</Tag>
            ))}
          </div>
          <Table
            size="small"
            loading={parsing}
            columns={previewColumns}
            dataSource={previewData}
            scroll={{ x: "max-content", y: 320 }}
            pagination={false}
          />
        </div>
      )}

      {/* Commit */}
      {fileList.length > 0 && !imported && (
        <div style={{ marginTop: 16 }}>
          <SaveButton onClick={onImport} loading={importing} text="Import employees" />
        </div>
      )}

      {errorNode}
      {imported && (
        <Alert
          style={{ marginTop: 16 }}
          type="success"
          showIcon
          message="Employees imported successfully. You can continue."
        />
      )}

      {existingCount > 0 ? (
        <>
          <div className="onb-section-h">Employees already in system</div>
          <Table
            size="small"
            style={{ marginBottom: 16 }}
            rowKey={(record) => record.id || record.employee_id || record.employment_id}
            columns={employeeColumns}
            dataSource={employeeRows}
            loading={employeeLoading || readinessLoading}
            pagination={{
              current: employeePagination.current,
              pageSize: employeePagination.pageSize,
              total: employeePagination.total,
              showSizeChanger: false,
            }}
            onChange={(pagination) => loadEmployees(pagination.current)}
            onRow={(record) => ({
              onClick: () => openEmployeeProfile(record),
              style: { cursor: "pointer" },
            })}
          />
        </>
      ) : null}

      <div className="onb-foot">
        <PreviousButton onClick={onBack} text="Back" />
        <span className="spacer" />
        <NextButton onClick={proceed} disabled={!canContinue} text="Continue" />
      </div>
      {childComponent}
    </>
  );
};

export default Step3Employees;
